import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listAdminUsers, setSuperAdmin, updateUser } from '../../api/admin';
import { ConfirmDialog } from '../ConfirmDialog';
import { EditUserDialog } from './EditUserDialog';
import type { AdminUserResponse } from '../../types/api';
import { useAuth } from '../../contexts/AuthContext';

export function AdminUsersTab() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const [pending, setPending] = useState<{ user: AdminUserResponse; enable: boolean } | null>(null);
  const [editing, setEditing] = useState<AdminUserResponse | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-users', page],
    queryFn: () => listAdminUsers(page, 50),
  });

  const mutation = useMutation({
    mutationFn: ({ userId, enabled }: { userId: string; enabled: boolean }) =>
      setSuperAdmin(userId, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setPending(null);
    },
  });

  if (isLoading) return <div className="p-4">Loading users…</div>;
  if (error) return <div className="p-4 text-red-500">Failed to load users.</div>;
  if (!data || data.items.length === 0) return <div className="p-4 text-slate-500">No users.</div>;

  const totalPages = Math.max(1, Math.ceil(data.totalCount / data.pageSize));

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
            <tr>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Clubs</th>
              <th className="px-3 py-2">SuperAdmin</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {data.items.map(u => (
              <tr key={u.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                <td className="px-3 py-2">{u.email}</td>
                <td className="px-3 py-2">{u.displayName}</td>
                <td className="px-3 py-2">{u.clubCount}</td>
                <td className="px-3 py-2">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={u.isSuperAdmin}
                      disabled={u.id === user?.id && u.isSuperAdmin}
                      onChange={(e) => setPending({ user: u, enable: e.target.checked })}
                      className="h-4 w-4"
                    />
                    <span className="text-xs">{u.isSuperAdmin ? 'Yes' : 'No'}</span>
                  </label>
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => setEditing(u)}
                    className="rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-xs hover:bg-slate-100 dark:hover:bg-slate-700"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm">
        <span className="text-slate-500">
          Page {data.page} of {totalPages} • {data.totalCount} users
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            className="rounded border px-3 py-1 disabled:opacity-50"
          >
            Prev
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            className="rounded border px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={pending !== null}
        title={pending?.enable ? 'Grant SuperAdmin?' : 'Revoke SuperAdmin?'}
        description={
          pending
            ? `${pending.enable ? 'Grant' : 'Revoke'} SuperAdmin for ${pending.user.email}?`
            : ''
        }
        confirmLabel={pending?.enable ? 'Grant' : 'Revoke'}
        confirmTone={pending?.enable ? 'default' : 'danger'}
        busy={mutation.isPending}
        onConfirm={() =>
          pending && mutation.mutate({ userId: pending.user.id, enabled: pending.enable })
        }
        onCancel={() => setPending(null)}
      />

      <EditUserDialog
        open={editing !== null}
        user={editing}
        onClose={() => setEditing(null)}
        onSave={async (body) => {
          if (!editing) return;
          await updateUser(editing.id, body);
          queryClient.invalidateQueries({ queryKey: ['admin-users'] });
        }}
      />
    </div>
  );
}
